import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BackofficeApiService, StaffUser } from './backoffice-api.service';

/**
 * Storage key is namespaced and deliberately distinct from the customer app's.
 *
 * This app is served from its own origin (backoffice.zeloapp.com), so its
 * localStorage is already isolated by the browser — that isolation is the whole
 * reason the portal is a subdomain rather than a path. The distinct key is a
 * second line of defence for anyone who later runs both on one host in dev.
 */
const TOKEN_KEY = 'zelo.backoffice.token';
const STAFF_KEY = 'zelo.backoffice.staff';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(BackofficeApiService);
  private readonly router = inject(Router);

  private readonly tokenSignal = signal<string | null>(
    localStorage.getItem(TOKEN_KEY)
  );
  private readonly staffSignal = signal<StaffUser | null>(readStoredStaff());

  readonly staff = this.staffSignal.asReadonly();
  readonly token = this.tokenSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.tokenSignal());

  /** Flag writes are operator-only; the UI hides what the API would refuse. */
  readonly canWrite = computed(() => this.staffSignal()?.role === 'operator');

  /**
   * Exchanges a Google ID token for a backoffice JWT.
   *
   * The Google credential is never stored — only the backoffice token the
   * backend mints from it, which is short-lived and revocable by deleting the
   * staff_users row.
   */
  async loginWithGoogle(idToken: string): Promise<void> {
    const result = await this.api.login(idToken);

    localStorage.setItem(TOKEN_KEY, result.token);
    localStorage.setItem(STAFF_KEY, JSON.stringify(result.staff));
    this.tokenSignal.set(result.token);
    this.staffSignal.set(result.staff);
  }

  logout(redirect = true): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(STAFF_KEY);
    this.tokenSignal.set(null);
    this.staffSignal.set(null);

    if (redirect) void this.router.navigate(['/login']);
  }
}

const readStoredStaff = (): StaffUser | null => {
  const raw = localStorage.getItem(STAFF_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as StaffUser;
  } catch {
    // A corrupt entry is not worth failing the app boot over — treat it as
    // logged out. The token is validated server-side on every request, so a
    // stale staff blob can never grant access on its own.
    return null;
  }
};
